import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { HelpCircle } from '@lifeomic/chromicons';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Popover } from '@lifeomic/chroma-react/components/Popover';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { useActiveProjectId, useNotify } from '../../hooks';
import { useApiClient } from '../../hooks/useRestAPIHooks';
import { getTestProps } from '../../util/testProps';
import { parseRejectionSource } from './memberExtractionRejectionReasons';
import { heroSpace, heroSurface } from './memberExtractionTheme';

const messages = defineMessages({
  reextract: {
    id: 'phc-web-toolkit.memberExtractions.reextract.action',
    defaultMessage: 'Re-extract',
  },
  reextracting: {
    id: 'phc-web-toolkit.memberExtractions.reextract.running',
    defaultMessage: 'Re-extracting',
  },
  helpLabel: {
    id: 'phc-web-toolkit.memberExtractions.reextract.helpLabel',
    defaultMessage: 'What re-extract does',
  },
  approvedQuestion: {
    id: 'phc-web-toolkit.memberExtractions.reextract.approvedQuestion',
    defaultMessage: 'What happens to results I approved?',
  },
  approvedAnswer: {
    id: 'phc-web-toolkit.memberExtractions.reextract.approvedAnswer',
    defaultMessage:
      'If the new run finds them again, they need review again. If not, they stay approved.',
  },
  rejectedQuestion: {
    id: 'phc-web-toolkit.memberExtractions.reextract.rejectedQuestion',
    defaultMessage: 'And ones I rejected?',
  },
  rejectedAnswer: {
    id: 'phc-web-toolkit.memberExtractions.reextract.rejectedAnswer',
    defaultMessage:
      'They stay deleted. The new run uses your rejection feedback to improve its results.',
  },
  started: {
    id: 'phc-web-toolkit.memberExtractions.reextract.started',
    defaultMessage: 'Re-extraction started.',
  },
  alreadyRunning: {
    id: 'phc-web-toolkit.memberExtractions.reextract.alreadyRunning',
    defaultMessage: 'This document is already being extracted.',
  },
  error: {
    id: 'phc-web-toolkit.memberExtractions.reextract.error',
    defaultMessage: 'Unable to re-extract this document.',
  },
});

const rainbowColors = [
  'hsl(0 100% 63%)',
  'hsl(90 100% 63%)',
  'hsl(210 100% 63%)',
  'hsl(195 100% 63%)',
  'hsl(270 100% 63%)',
];
const rainbowGradient = `linear-gradient(90deg, ${rainbowColors.join(', ')})`;

const rainbowButtonLayers = (fill: string) =>
  [
    `linear-gradient(${fill}, ${fill})`,
    `linear-gradient(${fill} 50%, color-mix(in srgb, ${fill} 60%, transparent) 80%, transparent)`,
    rainbowGradient,
  ].join(', ');

const useStyles = makeStyles(theme => ({
  '@keyframes rainbow': {
    '0%': { backgroundPosition: '0%' },
    '100%': { backgroundPosition: '200%' },
  },
  rainbowButton: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 36,
    padding: '0 16px',
    border: '2px solid transparent',
    borderRadius: 12,
    backgroundImage: rainbowButtonLayers(theme.palette.primary.main),
    backgroundSize: '200%',
    backgroundClip: 'padding-box, border-box, border-box',
    backgroundOrigin: 'border-box',
    color: theme.palette.primary.contrastText,
    font: 'inherit',
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    animation: '$rainbow 2s infinite linear',
    transition: `transform ${heroSurface.transition}`,
    '&::before': {
      content: '""',
      position: 'absolute',
      bottom: '-20%',
      left: '50%',
      zIndex: 0,
      width: '60%',
      height: '20%',
      transform: 'translateX(-50%)',
      background: rainbowGradient,
      backgroundSize: '200%',
      filter: 'blur(12px)',
      opacity: 0.8,
      animation: '$rainbow 2s infinite linear',
      transition: `width ${heroSurface.transition}, opacity ${heroSurface.transition}`,
    },
    '&:hover:not(:disabled)': {
      backgroundImage: rainbowButtonLayers(theme.palette.primary.dark),
      transform: 'translateY(-1px)',
      '&::before': {
        width: '80%',
        opacity: 1,
      },
    },
    '&:active:not(:disabled)': {
      transform: 'translateY(0)',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.5,
    },
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      transition: 'none',
      '&::before': {
        animation: 'none',
        transition: 'none',
      },
      '&:hover:not(:disabled)': {
        transform: 'none',
      },
    },
  },
  help: {
    maxWidth: 280,
  },
}));

const actionsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: heroSpace.tight,
  marginLeft: 'auto',
  flex: '0 0 auto',
};

const helpBodyStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: heroSpace.regular,
  padding: heroSpace.regular,
};

const questionStyle = {
  margin: 0,
};

const answerStyle = {
  margin: 0,
  color: heroSurface.textMuted,
};

const responseBody = (error: unknown) =>
  (
    error as {
      response?: {
        status?: number;
        data?: { task_id?: string; detail?: { task_id?: string } };
      };
    }
  ).response;

type MemberExtractionReextractProps = {
  patientId: string;
  sourceReference: string;
  running?: boolean;
  onStarted?: (taskId: string) => void;
};

export const MemberExtractionReextract: React.FC<
  MemberExtractionReextractProps
> = ({ patientId, sourceReference, running: taskRunning, onStarted }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const projectId = useActiveProjectId();
  const apiClient = useApiClient();
  const [notify] = useNotify();
  const [inFlight, setInFlight] = React.useState(false);
  const source = parseRejectionSource(sourceReference);

  if (!source) {
    return null;
  }

  const running = inFlight || Boolean(taskRunning);

  const reextract = () => {
    if (!projectId || running) {
      return;
    }
    setInFlight(true);
    apiClient
      .request('POST /v1/agents-v2/document-extraction-v2/invoke', {
        subject_id: patientId,
        project_id: projectId,
        source_resource_type: source.source_resource_type,
        source_resource_id: source.source_resource_id,
        ...(source.source_resource_type === 'DocumentReference'
          ? { source_document_reference_id: source.source_resource_id }
          : {}),
        include_clinic_only: true,
        force_regenerate: true,
        reason: 'PHC-UI extraction page re-extract',
        // The published client type omits QuestionnaireResponse sources.
      } as Parameters<typeof apiClient.request>[1])
      .then(response => {
        const id = (response as { data?: { task_id?: string } }).data?.task_id;
        if (id) {
          onStarted?.(id);
        }
        notify(formatMessage(messages.started), 'success');
      })
      .catch((error: unknown) => {
        const response = responseBody(error);
        if (response?.status === 409) {
          const id = response.data?.detail?.task_id ?? response.data?.task_id;
          if (id) {
            onStarted?.(id);
          }
          notify(formatMessage(messages.alreadyRunning), 'error');
          return;
        }
        notify(formatMessage(messages.error), 'error');
      })
      .finally(() => {
        setInFlight(false);
      });
  };

  return (
    <div style={actionsStyle}>
      <button
        type="button"
        className={classes.rainbowButton}
        disabled={running}
        onClick={reextract}
        {...getTestProps('member-extraction-reextract')}
      >
        {formatMessage(running ? messages.reextracting : messages.reextract)}
      </button>
      <Popover
        aria-label={formatMessage(messages.helpLabel)}
        className={classes.help}
        placement="bottom-end"
        usePortal
        anchorElement={
          <IconButton
            aria-label={formatMessage(messages.helpLabel)}
            icon={HelpCircle}
            size={0}
            {...getTestProps('member-extraction-reextract-help')}
          />
        }
      >
        <div style={helpBodyStyle}>
          <div>
            <Text size="caption" weight="bold" style={questionStyle}>
              {formatMessage(messages.approvedQuestion)}
            </Text>
            <Text size="caption" style={answerStyle}>
              {formatMessage(messages.approvedAnswer)}
            </Text>
          </div>
          <div>
            <Text size="caption" weight="bold" style={questionStyle}>
              {formatMessage(messages.rejectedQuestion)}
            </Text>
            <Text size="caption" style={answerStyle}>
              {formatMessage(messages.rejectedAnswer)}
            </Text>
          </div>
        </div>
      </Popover>
    </div>
  );
};
